import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can, ROLE_LABELS, ALL_ROLES, NAV_ITEMS } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { PageAccessDialog } from "@/components/PageAccessDialog";
import { AccessSummary } from "@/components/AccessSummary";
import { Plus, Mail, Loader2, Trash2, Copy, ShieldCheck, KeyRound, Pencil, Shield } from "lucide-react";

const EMPTY = { email: "", name: "", role: "gm", password: "", property_ids: [] };
// Pages that can be assigned to a role (mirrors the sidebar routes)
const PAGE_OPTIONS = NAV_ITEMS.map((i) => ({ to: i.to, label: i.label, section: i.section || "Main" }));
const DUTIES = [
  { key: "approve_invoices", label: "Approve Invoices" },
  { key: "print_checks", label: "Print Checks" },
  { key: "print_blank_checks", label: "Print Blank Checks" },
];

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const [props, setProps] = useState([]);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("create"); // create | invite
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const [permOpen, setPermOpen] = useState(false);
  const [permUser, setPermUser] = useState(null);
  const [permList, setPermList] = useState([]);
  const [permSaving, setPermSaving] = useState(false);

  // change password (admin)
  const [pwOpen, setPwOpen] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [pwValue, setPwValue] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const openPw = (u) => { setPwUser(u); setPwValue(""); setPwOpen(true); };
  const savePw = async () => {
    if (pwValue.length < 6) return toast.error("Password must be at least 6 characters");
    setPwSaving(true);
    try {
      await api.post(`/users/${pwUser.id}/set-password`, { password: pwValue });
      toast.success(`Password updated for ${pwUser.name || pwUser.email}`);
      setPwOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setPwSaving(false);
    }
  };

  const canManageDuties = can(user, ["admin", "regional"]);
  const [pageUser, setPageUser] = useState(null);
  const openPerms = (u) => {
    setPermUser(u);
    setPermList(u.permissions || []);
    setPermOpen(true);
  };
  const togglePerm = (key) =>
    setPermList((l) => (l.includes(key) ? l.filter((x) => x !== key) : [...l, key]));
  const savePerms = async () => {
    setPermSaving(true);
    try {
      await api.patch(`/users/${permUser.id}/permissions`, { permissions: permList });
      toast.success("Duties updated");
      setPermOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setPermSaving(false);
    }
  };

  // edit user (name / role / properties)
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", role: "gm", property_ids: [], can_create_tasks: false });
  const [editSaving, setEditSaving] = useState(false);
  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({ name: u.name || "", role: u.role, property_ids: u.property_ids || [], can_create_tasks: !!u.can_create_tasks });
    setEditOpen(true);
  };
  const setEF = (k, v) => setEditForm((f) => ({ ...f, [k]: v }));
  const toggleEditProp = (id) =>
    setEditForm((f) => ({
      ...f,
      property_ids: f.property_ids.includes(id) ? f.property_ids.filter((x) => x !== id) : [...f.property_ids, id],
    }));
  const saveEdit = async () => {
    setEditSaving(true);
    try {
      await api.patch(`/users/${editUser.id}`, { name: editForm.name, role: editForm.role, property_ids: editForm.property_ids, can_create_tasks: editForm.can_create_tasks });
      toast.success("User updated");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setEditSaving(false);
    }
  };

  const canCreate = can(user, ["admin", "regional"]);

  // roles (built-in + custom)
  const [roles, setRoles] = useState([]);
  const loadRoles = () => api.get("/roles").then((r) => setRoles(r.data)).catch(() => setRoles([]));
  const labelFor = (key) => roles.find((r) => r.key === key)?.label || ROLE_LABELS[key] || key;

  // role manager dialog
  const [roleOpen, setRoleOpen] = useState(false);
  const [roleEditing, setRoleEditing] = useState(null); // null = new
  const [roleForm, setRoleForm] = useState({ label: "", pages: ["/"] });
  const [roleSaving, setRoleSaving] = useState(false);
  const openNewRole = () => { setRoleEditing(null); setRoleForm({ label: "", pages: ["/"] }); setRoleOpen(true); };
  const openEditRole = (r) => { setRoleEditing(r); setRoleForm({ label: r.label, pages: r.pages || ["/"] }); setRoleOpen(true); };
  const toggleRolePage = (to) => setRoleForm((f) => ({
    ...f,
    pages: f.pages.includes(to) ? f.pages.filter((x) => x !== to) : [...f.pages, to],
  }));
  const saveRole = async () => {
    if (!roleEditing && !roleForm.label.trim()) return toast.error("Enter a role name");
    setRoleSaving(true);
    try {
      const pages = roleForm.pages.includes("/") ? roleForm.pages : ["/", ...roleForm.pages];
      if (roleEditing) {
        await api.patch(`/roles/${roleEditing.key}`, { label: roleForm.label, pages });
      } else {
        await api.post("/roles", { label: roleForm.label, pages });
      }
      toast.success("Role saved");
      setRoleOpen(false);
      loadRoles();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRoleSaving(false);
    }
  };
  const deleteRole = async (r) => {
    if (!window.confirm(`Delete the "${r.label}" role?`)) return;
    try {
      await api.delete(`/roles/${r.key}`);
      toast.success("Role deleted");
      loadRoles();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  useEffect(() => {
    load();
    loadRoles();
    api.get("/properties").then((r) => setProps(r.data)).catch(() => {});
  }, []);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleProp = (id) =>
    setForm((f) => ({
      ...f,
      property_ids: f.property_ids.includes(id)
        ? f.property_ids.filter((x) => x !== id)
        : [...f.property_ids, id],
    }));

  const openDialog = (m) => {
    setMode(m);
    setForm(EMPTY);
    setOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      if (mode === "create") {
        await api.post("/users", form);
        toast.success("User created");
      } else {
        const { data } = await api.post("/users/invite", {
          email: form.email,
          name: form.name,
          role: form.role,
          property_ids: form.property_ids,
        });
        const inviteLink = `${window.location.origin}/accept-invite?token=${data.token}`;
        await navigator.clipboard?.writeText(inviteLink).catch(() => {});
        toast.success("Invite created — link copied to clipboard", {
          description: inviteLink,
        });
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u) => {
    await api.patch(`/users/${u.id}`, { active: !u.active });
    load();
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const copyInviteLink = async (u) => {
    try {
      const { data } = await api.get(`/users/${u.id}/invite-token`);
      const inviteLink = `${window.location.origin}/accept-invite?token=${data.token}`;
      await navigator.clipboard?.writeText(inviteLink).catch(() => {});
      toast.success("Invite link copied to clipboard", { description: inviteLink });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (users === null) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="users-page">
      <PageAccessDialog target={pageUser} roleDoc={roles.find((r) => r.key === pageUser?.role)} onClose={() => setPageUser(null)} onSaved={load} />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps text-primary">Access</div>
          <h1 className="font-heading text-3xl font-black tracking-tight lg:text-4xl">User Management</h1>
          <p className="mt-1 text-sm text-muted-foreground">{users.length} users · manual creation & email invites.</p>
        </div>
        {canCreate && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => openDialog("invite")} data-testid="invite-user-btn">
              <Mail className="mr-2 h-4 w-4" /> Invite
            </Button>
            <Button onClick={() => openDialog("create")} data-testid="create-user-btn">
              <Plus className="mr-2 h-4 w-4" /> New User
            </Button>
          </div>
        )}
      </div>

      <Card className="rounded-none border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="text-muted-foreground">{u.email}</TableCell>
                <TableCell>
                  <Badge variant="secondary" className="rounded-none">{labelFor(u.role)}</Badge>
                </TableCell>
                <TableCell>
                  {u.status === "invited" ? (
                    <Badge className="rounded-none bg-[hsl(var(--warning))] text-black">Invited</Badge>
                  ) : u.active ? (
                    <Badge className="rounded-none bg-[hsl(var(--success))] text-white">Active</Badge>
                  ) : (
                    <Badge variant="destructive" className="rounded-none">Inactive</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {canCreate && u.id !== user.id && (
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}>
                        <Pencil className="mr-1 h-4 w-4" /> Edit
                      </Button>
                      {canManageDuties && u.role !== "admin" && (
                        <Button size="sm" variant="ghost" onClick={() => openPerms(u)} data-testid={`duties-user-${u.id}`}>
                          <ShieldCheck className="mr-1 h-4 w-4" /> Duties
                        </Button>
                      )}
                      {user.role === "admin" && u.role !== "admin" && (
                        <Button size="sm" variant="ghost" onClick={() => setPageUser(u)} data-testid={`page-access-user-${u.id}`}>
                          <Shield className="mr-1 h-4 w-4" /> Pages{Array.isArray(u.page_access) ? " ✱" : ""}
                        </Button>
                      )}
                      {u.status === "invited" && (
                        <Button size="sm" variant="ghost" onClick={() => copyInviteLink(u)} data-testid={`copy-invite-${u.id}`}>
                          <Copy className="mr-1 h-4 w-4" /> Copy link
                        </Button>
                      )}
                      {u.status !== "invited" && (
                        <Button size="sm" variant="ghost" onClick={() => toggleActive(u)}>
                          {u.active ? "Deactivate" : "Activate"}
                        </Button>
                      )}
                      {user.role === "admin" && (
                        <Button size="sm" variant="ghost" onClick={() => openPw(u)} data-testid={`set-password-${u.id}`}>
                          <KeyRound className="mr-1 h-4 w-4" /> Password
                        </Button>
                      )}
                      {user.role === "admin" && (
                        <Button size="sm" variant="ghost" onClick={() => remove(u)} data-testid={`delete-user-${u.id}`}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {user.role === "admin" && <AccessSummary users={users} roles={roles} props={props} onEdit={setPageUser} />}

      {/* Roles manager */}
      {canCreate && (
        <Card className="mt-8 rounded-none border" data-testid="roles-manager">
          <div className="flex items-center justify-between border-b border-border p-4">
            <div>
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold"><Shield className="h-4 w-4" /> Roles &amp; Access</h2>
              <p className="mt-0.5 text-xs text-muted-foreground">Control which screens each role can see. Add your own roles (e.g. Sales) with custom access.</p>
            </div>
            <Button onClick={openNewRole} data-testid="add-role-btn"><Plus className="mr-2 h-4 w-4" /> Add Role</Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Role</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Screens</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roles.map((r) => (
                <TableRow key={r.key} data-testid={`role-row-${r.key}`}>
                  <TableCell className="font-medium">{r.label}</TableCell>
                  <TableCell>
                    {r.builtin
                      ? <Badge variant="secondary" className="rounded-none">Built-in</Badge>
                      : <Badge className="rounded-none bg-primary text-primary-foreground">Custom</Badge>}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{(r.pages || []).length} screen{(r.pages || []).length === 1 ? "" : "s"}</TableCell>
                  <TableCell className="text-right">
                    {r.key !== "admin" && (
                      <Button size="sm" variant="ghost" onClick={() => openEditRole(r)} data-testid={`edit-role-${r.key}`}>
                        <Pencil className="mr-1 h-4 w-4" /> {r.builtin ? "Access" : "Edit"}
                      </Button>
                    )}
                    {!r.builtin && (
                      <Button size="icon" variant="ghost" onClick={() => deleteRole(r)} data-testid={`delete-role-${r.key}`}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={roleOpen} onOpenChange={setRoleOpen}>
        <DialogContent data-testid="role-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{roleEditing ? (roleEditing.builtin ? `${roleEditing.label} — Screen Access` : "Edit Role") : "Add Role"}</DialogTitle>
            <DialogDescription>Choose which screens this role can open. Write actions on sensitive pages stay limited to management roles.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Role Name</Label>
              <Input value={roleForm.label} disabled={roleEditing?.builtin}
                onChange={(e) => setRoleForm((f) => ({ ...f, label: e.target.value }))}
                placeholder="e.g. Sales" data-testid="role-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Screens this role can access</Label>
              <div className="max-h-64 space-y-1.5 overflow-y-auto border border-border p-3">
                {PAGE_OPTIONS.map((p) => (
                  <label key={p.to} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={roleForm.pages.includes(p.to)}
                      disabled={p.to === "/"}
                      onCheckedChange={() => toggleRolePage(p.to)}
                      data-testid={`role-page-${p.to === "/" ? "dashboard" : p.to.slice(1)}`}
                    />
                    {p.label}
                    {p.to === "/" && <span className="text-xs text-muted-foreground">(always on)</span>}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleOpen(false)}>Cancel</Button>
            <Button onClick={saveRole} disabled={roleSaving} data-testid="save-role-btn">
              {roleSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{mode === "create" ? "Create User" : "Invite User"}</DialogTitle>
            <DialogDescription>
              {mode === "create"
                ? "Create an account with an initial password."
                : "Send an onboarding link so the user can set their own password."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Full Name</Label>
                <Input value={form.name} onChange={(e) => setF("name", e.target.value)} data-testid="user-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setF("email", e.target.value)} data-testid="user-email" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setF("role", v)}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => (
                      <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {mode === "create" && (
                <div className="space-y-1.5">
                  <Label>Initial Password</Label>
                  <Input type="text" value={form.password} onChange={(e) => setF("password", e.target.value)} data-testid="user-password" />
                </div>
              )}
            </div>
            {props.length > 0 && (
              <div className="space-y-2">
                <Label>Assigned Properties</Label>
                <div className="max-h-40 space-y-2 overflow-y-auto border border-border p-3">
                  {props.map((p) => (
                    <label key={p.id} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={form.property_ids.includes(p.id)} onCheckedChange={() => toggleProp(p.id)} />
                      {p.name}
                    </label>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">Managers & owners only see assigned properties.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={saving || !form.email || !form.name || (mode === "create" && !form.password)}
              data-testid="submit-user-btn"
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {mode === "create" ? "Create" : (<><Copy className="mr-2 h-4 w-4" />Generate Invite</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={permOpen} onOpenChange={setPermOpen}>
        <DialogContent data-testid="duties-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Assign Duties</DialogTitle>
            <DialogDescription>
              Grant {permUser?.name} permission to approve invoices and/or print checks.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {DUTIES.map((d) => (
              <label key={d.key} className="flex items-center gap-3 border border-border p-3 text-sm">
                <Checkbox
                  checked={permList.includes(d.key)}
                  onCheckedChange={() => togglePerm(d.key)}
                  data-testid={`duty-${d.key}`}
                />
                {d.label}
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermOpen(false)}>Cancel</Button>
            <Button onClick={savePerms} disabled={permSaving} data-testid="save-duties-btn">
              {permSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save Duties
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent data-testid="set-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Change Password</DialogTitle>
            <DialogDescription>Set a new password for {pwUser?.name || pwUser?.email}. It takes effect immediately.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label>New Password</Label>
            <Input
              type="text"
              value={pwValue}
              onChange={(e) => setPwValue(e.target.value)}
              placeholder="At least 6 characters"
              data-testid="new-password-input"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(false)}>Cancel</Button>
            <Button onClick={savePw} disabled={pwSaving || pwValue.length < 6} data-testid="save-password-btn">
              {pwSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Set Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit User</DialogTitle>
            <DialogDescription>Update {editUser?.email}'s name, role/permission and assigned properties.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Full Name</Label>
              <Input value={editForm.name} onChange={(e) => setEF("name", e.target.value)} data-testid="edit-user-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Role / Permission</Label>
              <Select value={editForm.role} onValueChange={(v) => setEF("role", v)}>
                <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {roles.map((r) => (<SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-start gap-3 border border-border p-3 text-sm" data-testid="edit-user-cancreatetasks-row">
              <Checkbox
                checked={["admin", "regional"].includes(editForm.role) || editForm.can_create_tasks}
                disabled={["admin", "regional"].includes(editForm.role)}
                onCheckedChange={(v) => setEF("can_create_tasks", !!v)}
                data-testid="edit-user-cancreatetasks"
              />
              <span>
                <span className="font-medium">Can create &amp; assign tasks</span>
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  {["admin", "regional"].includes(editForm.role)
                    ? "Always on for Administrators and Regional Managers."
                    : "Let this user create tasks and assign them to people or roles."}
                </span>
              </span>
            </label>
            {props.length > 0 && (
              <div className="space-y-2">
                <Label>Assigned Properties</Label>
                <div className="max-h-40 space-y-2 overflow-y-auto border border-border p-3">
                  {props.map((p) => (
                    <label key={p.id} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={editForm.property_ids.includes(p.id)} onCheckedChange={() => toggleEditProp(p.id)} data-testid={`edit-prop-${p.id}`} />
                      {p.name}
                    </label>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">Add or remove property access. Managers & owners only see assigned properties.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={editSaving || !editForm.name} data-testid="save-edit-user-btn">
              {editSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
